/**
 * The racer roster.
 *
 * `id` doubles as the Phaser texture key and the folder name under
 * `assets/voice/<id>/`, so it must stay lowercase and filesystem-safe.
 *
 * Every racer flies differently. The multipliers are deliberately modest —
 * enough that a regular player feels the difference and forms a preference,
 * not so much that one pick is simply correct.
 */
export const CHARACTER_IDS = ['woofus', 'puurd', 'ribbert', 'humble'] as const;

export type CharacterId = (typeof CHARACTER_IDS)[number];

export interface Character {
  id: CharacterId;
  /** Name shown under the portrait on the player select screen. */
  displayName: string;
  /** One line on how they fly, shown on the select screen. */
  tagline: string;
  /** Voice lines are only recorded for Woofus so far. See ROADMAP.md. */
  hasVoice: boolean;
  /** Scales top speed and acceleration. */
  speed: number;
  /** Scales the collision radius. Smaller is harder to hit. */
  size: number;
  /** Scales how long the boost meter lasts. */
  boost: number;
}

export const ROSTER: readonly Character[] = [
  {
    id: 'woofus',
    displayName: 'Woofus',
    tagline: 'All-rounder',
    hasVoice: true,
    speed: 1,
    size: 1,
    boost: 1,
  },
  {
    id: 'puurd',
    displayName: 'Puurd',
    tagline: 'Quick, but a big target',
    hasVoice: false,
    speed: 1.18,
    size: 1.15,
    boost: 0.9,
  },
  {
    id: 'ribbert',
    displayName: 'Ribbert',
    tagline: 'Tiny and slippery',
    hasVoice: false,
    speed: 0.92,
    size: 0.78,
    boost: 1,
  },
  {
    id: 'humble',
    displayName: 'Humble',
    tagline: 'Boosts forever',
    hasVoice: false,
    speed: 0.96,
    size: 1.05,
    boost: 1.6,
  },
];

export function isCharacterId(value: string): value is CharacterId {
  return (CHARACTER_IDS as readonly string[]).includes(value);
}

export function getCharacter(id: CharacterId): Character {
  const character = ROSTER.find((entry) => entry.id === id);
  if (!character) {
    throw new Error(`Unknown character: ${id}`);
  }
  return character;
}
